import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';
import { applyOAuthDeepLink } from './oauthDeepLink';
import { installMobileAudioGestureUnlock } from './mobileAudioUnlock';

/**
 * Capacitor-only: splash + status bar, OAuth deep link + cold start URL, optional audio gesture prime.
 */
export function CapacitorNativeBridge({ setUser }) {
  const navigate = useNavigate();
  const removeListenerRef = useRef(null);

  useEffect(() => {
    const audioCleanup = installMobileAudioGestureUnlock();

    if (!Capacitor.isNativePlatform()) {
      return () => {
        audioCleanup();
      };
    }

    let cancelled = false;

    (async () => {
      await SplashScreen.hide().catch(() => {});
      await StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
      await StatusBar.setOverlaysWebView({ overlay: false }).catch(() => {});

      const handleUrl = (url) => {
        if (!url) return;
        applyOAuthDeepLink(url, { setUser, navigate });
      };

      try {
        const launch = await App.getLaunchUrl();
        if (!cancelled && launch && launch.url) {
          handleUrl(launch.url);
        }
      } catch (e) {
        console.warn('Capacitor getLaunchUrl:', e.message || e);
      }

      if (cancelled) {
        return;
      }

      const sub = await App.addListener('appUrlOpen', (event) => {
        handleUrl(event.url);
      });

      if (cancelled) {
        await sub.remove();
        return;
      }

      removeListenerRef.current = () => {
        sub.remove();
        removeListenerRef.current = null;
      };
    })();

    return () => {
      cancelled = true;
      if (removeListenerRef.current) {
        removeListenerRef.current();
      }
      audioCleanup();
    };
  }, [navigate, setUser]);

  return null;
}
